import { Helmet } from "react-helmet";
import cn from "classnames";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

// store
import useClaimBusinessStore from "store/useClaimBusinessStore";
import useAuthStore from "store/useAuthStore";

// components
import { Alert, Icon } from "@trussworks/react-uswds";
import { Button } from "components/LibraryComponents/Components";
import VerifyBusinessErrorDialog from "./VerifyBusinessErrorDialog";

// styles
import styles from "./VerifyBusiness.module.scss";

type Business = {
  name?: string;
  business_name: string;
  trade_name: string;
  uei: string;
  business_address: string;
  government_contact: string;
  structure?: string;
  entity?: string;
};

export default function VerifyBusiness() {
  const navigate = useNavigate();

  const { claimBusiness, claimRequestId } = useClaimBusinessStore();
  const { updateUserFromToken } = useAuthStore();

  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isOpen, setIsOpen] = useState<boolean>(false);

  // Get initial state and subscribe to changes
  useEffect(() => {
    // Get initial state
    const { data = [] } = useClaimBusinessStore.getState();

    // If no data, redirect to claim business page
    if (!data || data?.length === 0) {
      navigate("/claim-business");
    }
    // Set initial businesses state
    setBusinesses(data);

    // Subscribe to store changes
    const unsubscribe = useClaimBusinessStore.subscribe((state) => {
      setBusinesses(state.data || []);
    });

    // Cleanup subscription on unmount
    return () => {
      unsubscribe();
    };
  }, []);

  // Handle claim business action
  const handleSubmit = async () => {
    if (businesses.length === 0) return;

    try {
      setIsLoading(true);

      let response: any = await claimBusiness({
        claimRequestId,
        structure: businesses[0]?.structure || "llc", // Default to "llc" if structure is not provided
      });

      if (response?.success) {
        // update user with new business data
        updateUserFromToken(businesses);

        navigate("/participation-agreement");
      }
    } catch (error) {
      console.error("Failed to claim business:", error);
      toast.error("Failed to claim business");

      setErrorMessage(error.message);
      setIsOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Verify Business | Small Business Search</title>
      </Helmet>

      <div className={styles["verify-business-root"]}>
        <h1 className={styles["heading"]}>Verify your business(es)</h1>

        <div className={styles["main-content"]}>
          <div className={styles["space-y-2"]}>
            <p className={styles["desc"]}>
              Below are the SAM registrations linked to your Taxpayer
              Identification Numbers (TIN). Review the information and claim the
              business if it is correct.
            </p>

            <Alert
              type={"info"}
              headingLevel={"h4"}
              className={styles["alert-wrapper"]}
            >
              <p className={styles["alert-text"]}>
                If any of the information here is incorrect, go to{" "}
                <a href="https://www.sba.gov/" rel="noreferrer" target="_blank">
                  SAM.gov
                </a>{" "}
                to make corrections. After your updates have been reviewed and
                activated by SAM.gov, the changes will appear here within 3
                business days.
              </p>
            </Alert>
          </div>

          <div className={styles["section"]}>
            {businesses?.map((data, idx) => (
              <BusinessItem key={idx} data={data} />
            ))}
          </div>

          <div className={styles["button-wrapper"]}>
            <Button
              type="button"
              loading={isLoading}
              disabled={isLoading}
              className={styles["submit-btn"]}
              onClick={handleSubmit}
            >
              {businesses?.length > 1
                ? "Claim businesses"
                : "Claim this business"}
            </Button>
          </div>
        </div>
      </div>

      {isOpen && (
        <VerifyBusinessErrorDialog
          open={isOpen}
          errorMessage={errorMessage}
          setOpen={setIsOpen}
        />
      )}
    </>
  );
}

function BusinessItem({ data }: { data: Business }) {
  const [open, setOpen] = useState<boolean>(true);

  return (
    <div className={styles["business-item"]}>
      <button
        type="button"
        className={cn(styles["btn-toggle"], { [styles["open"]]: open })}
        onClick={() => setOpen(!open)}
      >
        <p className={styles["btn-text"]}>{data.name}</p>
        <Icon.ArrowDropDown aria-label="dropdown arrow" />
      </button>

      <ul hidden={!open} className={styles["content"]}>
        <li className={styles["item"]}>
          <p>Business name</p>
          <p>{data.business_name}</p>
        </li>
        <li className={styles["item"]}>
          <p>Trade name/“Doing business as”</p>
          <p>{data.trade_name}</p>
        </li>
        <li className={styles["item"]}>
          <p>Business UEI</p>
          <p>{data.uei}</p>
        </li>
        <li className={styles["item"]}>
          <p>Business address</p>
          <p>{data.business_address}</p>
        </li>
        <li className={styles["item"]}>
          <p>Government contact</p>
          <p>{data.government_contact}</p>
        </li>
        <li className={styles["item"]}>
          <p>Business structure</p>
          <p>{data.structure}</p>
        </li>
        <li className={styles["item"]}>
          <p>Entity-Owned</p>
          <p>{data.entity}</p>
        </li>
      </ul>
    </div>
  );
}
